// src/App.tsx
import React from "react";
import { Provider } from "react-redux";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { store } from "./app/store";
import LoginPage from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import { loadFromStorage } from "./slices/authSlice";
import HomePage from "./pages/HomePage";
import SalesPage from "./pages/SalesPage";
import PreviewPage from "./pages/PreviewPage";
import RegisterPage from "./pages/RegisterPage";
import MainLayout from "./layouts/MainLayout";
import AdminLayout from "./layouts/AdminLayout";
import { JSX } from "react";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import VerifyResetPage from "./pages/VerifyResetPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import LessonPage from "./pages/LessonPage"; // Secure video lesson page
import CourseDetailPage from "./pages/CourseDetailPage"; // Course overview page
import SettingsPage from "./pages/SettingsPage";
import StudentLayout from "./layouts/StudentLayout";
import AdminDashboard from "./pages/AdminDashboard";
import AdminCourses from "./pages/admin/AdminCourses";

// --- CRITICAL: synchronously load persisted auth into the store BEFORE rendering ---
store.dispatch(loadFromStorage());
// ------------------------------------------------------------------------------

/**
 * ProtectedRoute component - guards student/user routes
 */
function ProtectedRoute({ children }: { children: JSX.Element }) {
  const token = (store.getState() as any).auth.token; // sync read from store is fine here
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

/**
 * AdminRoute - guards admin-only routes
 */
function AdminRoute({ children }: { children: JSX.Element }) {
  const state = store.getState() as any;
  const token = state.auth.token;
  const role = state.auth.role;

  if (!token) return <Navigate to="/login" replace />;
  if (role !== "admin") return <Navigate to="/dashboard" replace />;

  return children;
}

function AppWrapperInner() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<MainLayout />}>
          <Route index element={<HomePage />} />
          <Route path="sl" element={<SalesPage />} />
          <Route path="preview" element={<PreviewPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="login" element={<LoginPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="verify-reset" element={<VerifyResetPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />
        </Route>

        {/* Student Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <StudentLayout />
            </ProtectedRoute>
          }
        >
          {/* Dashboard home */}
          <Route index element={<Dashboard />} />

          {/* Course detail page - view modules and lessons */}
          <Route path="courses/:courseId" element={<CourseDetailPage />} />

          {/* Lesson route - watch video */}
          <Route path="lesson/:lessonId" element={<LessonPage />} />

          {/* Settings */}
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* Admin Protected Routes */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          {/* Admin dashboard home */}
          <Route index element={<AdminDashboard />} />

          {/* Admin courses management */}
          <Route path="courses" element={<AdminCourses />} />
        </Route>

        {/* Catch-all redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <Provider store={store}>
      <AppWrapperInner />
    </Provider>
  );
}
